import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { API } from '../api';
import { capabilityApi } from '../api/capability';
import { useProgramContext } from '../hooks/useProgramContext';
import { getLLAuthHeaders } from '../pages/new-designs/utils/llAuthHeaders';

// The Capability backend, read through react-query: the learner's own levels,
// Roleplays and Assessments, and the admin's dashboard, Members, Insights and
// Operations. Writes go straight through capabilityApi and then refresh what
// they change with useRefreshCapability().

const hasTokens = () => {
  try {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return Boolean(tokens?.id_token && tokens?.access_token);
  } catch {
    return false;
  }
};

const MINE = ['capability', 'me'] as const;
const ADMIN = ['capability', 'admin'] as const;
const teamsKey = (teamIds?: string[] | null) => (teamIds?.length ? [...teamIds].sort().join(',') : 'all');

export const capabilityKeys = {
  mine: [...MINE, 'capability'] as const,
  myTrend: [...MINE, 'trend'] as const,
  myActivity: [...MINE, 'activity'] as const,
  myActivityHistory: [...MINE, 'activity-history'] as const,
  myAssessments: [...MINE, 'assessments'] as const,
  myAssessment: (id: string) => [...MINE, 'assessment', id] as const,
  myResults: (id: string) => [...MINE, 'results', id] as const,
  admin: (what: string, org: string | undefined, extra = '') => [...ADMIN, what, org || '', extra] as const
};

/** The org a platform admin is looking at; undefined for everyone else (their token carries it). */
export const useCapabilityOrg = (): string | undefined => {
  const { orgId, isPlatformAdmin } = useProgramContext();
  return isPlatformAdmin ? orgId : undefined;
};

const STALE = 30_000;

// ─── The learner's own ──────────────────────────────────────────────────────

export const useMyCapability = (enabled = true) =>
  useQuery({
    queryKey: capabilityKeys.mine,
    queryFn: async () => (await capabilityApi.myCapability()).capability,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });

export const useMyTrend = (enabled = true) =>
  useQuery({
    queryKey: capabilityKeys.myTrend,
    queryFn: capabilityApi.myTrend,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });

export const useMyActivity = (enabled = true) =>
  useQuery({
    queryKey: capabilityKeys.myActivity,
    queryFn: capabilityApi.myActivity,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });

/** The learner's Activity trend, a point a week (Last 6 Months). */
export const useMyActivityHistory = (enabled = true) =>
  useQuery({
    queryKey: capabilityKeys.myActivityHistory,
    queryFn: capabilityApi.myActivityHistory,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });

export const useMyAssessments = (enabled = true) =>
  useQuery({
    queryKey: capabilityKeys.myAssessments,
    queryFn: async () => (await capabilityApi.myAssessments()).assessments,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });

export const useMyAssessment = (assessmentId: string | undefined) =>
  useQuery({
    queryKey: capabilityKeys.myAssessment(assessmentId || ''),
    queryFn: () => capabilityApi.myAssessment(assessmentId as string),
    enabled: Boolean(assessmentId) && hasTokens(),
    retry: false
  });

export const useMyAssessmentResults = (assessmentId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: capabilityKeys.myResults(assessmentId || ''),
    queryFn: () => capabilityApi.myAssessmentResults(assessmentId as string),
    enabled: enabled && Boolean(assessmentId) && hasTokens(),
    retry: false
  });

// ─── The admin's ────────────────────────────────────────────────────────────

export const useCapabilityPairs = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('pairs', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.pairs(teamIds, org),
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useCapabilityLevels = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('levels', org, teamsKey(teamIds)),
    queryFn: async () => (await capabilityApi.levels(teamIds, org)).rows,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

/** The Trainings Elliott recommends for the teams in view (the ones the viewer may change). */
export const useCapabilityRecommendations = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('recommendations', org, teamsKey(teamIds)),
    queryFn: async () => (await capabilityApi.recommendations(teamIds, org)).recommendations,
    enabled: enabled && hasTokens(),
    staleTime: STALE,
    // A refusal (or a backend without the endpoint yet) answers the same the second time.
    retry: false
  });
};

/**
 * The names of the org's capability roles, by id (GET /v3/admin/capability-setup):
 * what a program for roles shows as its audience. One read for every card.
 */
export const useCapabilityRoleNames = (enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('roleNames', org),
    queryFn: async () => {
      const res: any = await API.getCapabilitySetup({ ...getLLAuthHeaders(), orgId: org });
      const data = res?.data?.data ?? res?.data ?? {};
      const roles: any[] = Array.isArray(data.roles) ? data.roles : [];
      return new Map<string, string>(roles.map((r) => [String(r.id), String(r.name)]));
    },
    enabled: enabled && hasTokens(),
    staleTime: STALE,
    // A viewer who may not read Capability Setup gets the same refusal again: the roles are counted instead.
    retry: false
  });
};

export const useCapabilityTrend = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('trend', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.trend(teamIds, org),
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

/** The Activity trend for the teams (the org with none), a point a week. */
export const useCapabilityActivityHistory = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('activity-history', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.activityHistory(teamIds, org),
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

/** A member's Activity trend, as an admin sees it in Members. */
export const useMemberActivityHistory = (userId: string | null | undefined, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('member-activity-history', org, userId || ''),
    queryFn: () => capabilityApi.memberActivityHistory(userId as string, org),
    enabled: enabled && Boolean(userId) && hasTokens(),
    staleTime: STALE
  });
};

export const useCapabilityActivity = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('activity', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.activity(teamIds, org),
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useCapabilityMembers = (enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('members', org),
    queryFn: async () => (await capabilityApi.members(org)).members,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useCapabilityMember = (userId: string | null | undefined) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('member', org, userId || ''),
    queryFn: () => capabilityApi.member(userId as string, org),
    enabled: Boolean(userId) && hasTokens(),
    retry: false
  });
};

export const useMemberConversations = (userId: string | null | undefined) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('conversations', org, userId || ''),
    queryFn: () => capabilityApi.memberConversations(userId as string, org),
    enabled: Boolean(userId) && hasTokens(),
    retry: false
  });
};

export const useCapabilityInsights = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('insights', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.insights(teamIds, org),
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

export const useCapabilityOperations = (teamIds: string[] | null, enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('operations', org, teamsKey(teamIds)),
    queryFn: () => capabilityApi.operations(teamIds, org),
    enabled: enabled && hasTokens()
  });
};

export const useCapabilityAssessments = (enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('assessments', org),
    queryFn: async () => (await capabilityApi.assessments(org)).assessments,
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

/**
 * The Assessments still being built that the viewer may see: every one in the
 * org for an org admin, those measuring one of their teams for a team manager.
 * None from a backend that does not keep them yet (it answers 404).
 */
export const useCapabilityAssessmentDrafts = (enabled = true) => {
  const org = useCapabilityOrg();
  return useQuery({
    queryKey: capabilityKeys.admin('assessment-drafts', org),
    queryFn: async () => {
      try {
        return (await capabilityApi.assessmentDrafts(org)).drafts;
      } catch (err: any) {
        if (err?.response?.status === 404) return [];
        throw err;
      }
    },
    enabled: enabled && hasTokens(),
    staleTime: STALE
  });
};

/** After one is kept or discarded: re-read them, and nothing else. */
export const useRefreshAssessmentDrafts = () => {
  const client = useQueryClient();
  const org = useCapabilityOrg();
  return useCallback(() => {
    client.invalidateQueries({ queryKey: capabilityKeys.admin('assessment-drafts', org) });
  }, [client, org]);
};

/** After a write: re-read the learner's own data, the admin's, or both. */
export const useRefreshCapability = () => {
  const client = useQueryClient();
  return useCallback(
    (which: 'mine' | 'admin' | 'all' = 'all') => {
      if (which !== 'admin') client.invalidateQueries({ queryKey: MINE });
      if (which !== 'mine') client.invalidateQueries({ queryKey: ADMIN });
    },
    [client]
  );
};
